import { Button, IdOptionType, SelectList } from "shared";
import { YandexListTypes } from "entities/template";
import type { ILegalOrgListItem } from "entities/legal_orgs";
import React, { useEffect, useMemo, useState } from "react";
import { useComponentsTranslations } from "../../../../../lib/i18n";
import { buildAdditionalCompaniesOptionOrgs } from "../../../../../lib/utils/legal_orgs_scope_selection";
import styles from "./additional_companies_modal.module.scss";

type TScopeListMode = YandexListTypes.yaBGs | YandexListTypes.yaBUs | YandexListTypes.yaCountries;

interface IAdditionalCompaniesModalProps {
    close: () => void;
    allOrgs: ILegalOrgListItem[];
    optionsList: TScopeListMode;
    scopeList: string[];
    additionalCompanyIds: string[];
    onSave: (yaIds: string[]) => void;
}

export const AdditionalCompaniesModal: React.FC<IAdditionalCompaniesModalProps> = React.memo(
    ({ close, allOrgs, optionsList, scopeList, additionalCompanyIds, onSave }) => {
        const t = useComponentsTranslations();
        const [selectedYaIds, setSelectedYaIds] = useState<string[]>([]);

        const optionOrgs = useMemo(
            () =>
                buildAdditionalCompaniesOptionOrgs(allOrgs, optionsList, scopeList, additionalCompanyIds),
            [allOrgs, optionsList, scopeList, additionalCompanyIds]
        );

        const selectOptions: IdOptionType[] = useMemo(
            () =>
                optionOrgs.map((o) => ({
                    value: o.ya_id,
                    label: o.ya_name,
                    id: o.ya_id,
                })),
            [optionOrgs]
        );

        const optionOrgIdsKey = useMemo(
            () => optionOrgs.map((o) => o.ya_id).sort().join("\0"),
            [optionOrgs]
        );

        const additionalKey = useMemo(
            () => [...additionalCompanyIds].sort().join("\0"),
            [additionalCompanyIds]
        );

        useEffect(() => {
            const allowed = new Set(optionOrgs.map((o) => o.ya_id));
            setSelectedYaIds(additionalCompanyIds.filter((id) => allowed.has(id)));
        }, [additionalKey, optionOrgIdsKey]);

        const resetFactor = `${optionsList}|${scopeList.join(",")}|${selectOptions.length}|${selectOptions.map((o) => o.value).join(",")}`;

        const onSaveClick = () => {
            onSave(selectedYaIds);
            close();
        };

        return (
            <div className={styles.container}>
                {selectOptions.length === 0 && (
                    <div className={styles.empty}>{t("additionalCompaniesModalEmpty")}</div>
                )}
                {selectOptions.length > 0 && (
                    <>
                        <div className={styles.hint}>{t("additionalCompaniesModalHint")}</div>
                        <SelectList
                            draggable={false}
                            value={selectedYaIds}
                            options={selectOptions}
                            resetFactor={resetFactor}
                            onChange={setSelectedYaIds}
                            addButtonLabel={t("legalCompanyListAddCompany")}
                        />
                        <div className={styles.buttonGroup}>
                            <Button view="legal" onClick={onSaveClick}>
                                {t("save")}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        );
    }
);
